import React, { useState } from 'react';
import { X, Plus, Edit3, Save, Trash2 } from 'lucide-react';

const EbesClausesManager = ({ formData, updateFormData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newClause, setNewClause] = useState({ title: '', content: '' });
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingClause, setEditingClause] = useState({ title: '', content: '' });

  const customClauses = formData.customClauses || [];

  const addClause = () => {
    if (!newClause.title.trim() || !newClause.content.trim()) return;

    const updatedClauses = [
      ...customClauses,
      {
        id: Date.now().toString(),
        title: newClause.title.trim(),
        content: newClause.content.trim()
      }
    ];

    updateFormData({
      ...formData,
      customClauses: updatedClauses
    });

    setNewClause({ title: '', content: '' });
  };

  const removeClause = (index) => {
    const updatedClauses = customClauses.filter((_, i) => i !== index);
    updateFormData({
      ...formData,
      customClauses: updatedClauses
    });
  };

  const startEditing = (index, clause) => {
    setEditingIndex(index);
    setEditingClause({ title: clause.title, content: clause.content });
  };

  const saveEdit = () => {
    if (!editingClause.title.trim() || !editingClause.content.trim()) return;

    const updatedClauses = customClauses.map((clause, index) => 
      index === editingIndex 
        ? { ...clause, title: editingClause.title.trim(), content: editingClause.content.trim() }
        : clause
    );

    updateFormData({
      ...formData,
      customClauses: updatedClauses
    });

    setEditingIndex(-1);
    setEditingClause({ title: '', content: '' });
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditingClause({ title: '', content: '' });
  };

  const defaultClauses = [
    {
      id: 'responsabilidades',
      title: 'DAS RESPONSABILIDADES E DIREITOS DAS PARTES',
      content: 'Constituem direitos do CLIENTE o recebimento do objeto deste CONTRATO e a sua fiscalização, nas condições avençadas, inclusive no tocante à garantia. E, da EBES SOLAR, o recebimento do preço ajustado na forma e no prazo acordados.',
      isDefault: true
    },
    {
      id: 'disposicoes_gerais',
      title: 'DAS DISPOSIÇÕES GERAIS',
      content: 'O CLIENTE declara estar ciente de que a USINA objeto deste contrato é conectada diretamente à rede da concessionária e tem como função exclusiva a geração de energia elétrica, estando seu bom ou mau desempenho diretamente relacionado à ocorrência de condições climáticas favoráveis.',
      isDefault: true
    }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          📄 Cláusulas do Contrato
        </h3>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
        >
          {isExpanded ? 'Recolher' : 'Expandir'} Cláusulas
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-6">
          {/* Cláusulas Padrão */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">Cláusulas Padrão (sempre incluídas)</h4>
            <div className="space-y-3">
              {defaultClauses.map((clause, index) => (
                <div key={clause.id} className="bg-gray-50 p-4 rounded-lg border">
                  <h5 className="font-semibold text-gray-900 text-sm mb-2">
                    {index + 1}. {clause.title}
                  </h5>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {clause.content}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Cláusulas Personalizadas */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">
              Cláusulas Personalizadas ({customClauses.length})
            </h4>
            
            {/* Lista de cláusulas existentes */}
            {customClauses.length > 0 && (
              <div className="space-y-3 mb-4">
                {customClauses.map((clause, index) => (
                  <div key={clause.id} className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    {editingIndex === index ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="Título da cláusula"
                          value={editingClause.title}
                          onChange={(e) => setEditingClause({ ...editingClause, title: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm font-medium"
                        />
                        <textarea
                          placeholder="Conteúdo da cláusula"
                          value={editingClause.content}
                          onChange={(e) => setEditingClause({ ...editingClause, content: e.target.value })}
                          rows={4}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={saveEdit}
                            disabled={!editingClause.title.trim() || !editingClause.content.trim()}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Save className="w-3 h-3" />
                            Salvar
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700"
                          >
                            <X className="w-3 h-3" />
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-semibold text-gray-900 text-sm">
                            {defaultClauses.length + index + 1}. {clause.title}
                          </h5>
                          <div className="flex gap-1 ml-2">
                            <button
                              type="button"
                              onClick={() => startEditing(index, clause)}
                              className="text-blue-600 hover:text-blue-700 p-1"
                              title="Editar cláusula"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeClause(index)}
                              className="text-red-600 hover:text-red-700 p-1"
                              title="Remover cláusula"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {clause.content}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Adicionar nova cláusula */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h5 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Adicionar Nova Cláusula
              </h5>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Ex: DAS PENALIDADES"
                  value={newClause.title}
                  onChange={(e) => setNewClause({ ...newClause, title: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm font-medium"
                  maxLength={100}
                />
                <textarea
                  placeholder="Digite o conteúdo da cláusula..."
                  value={newClause.content}
                  onChange={(e) => setNewClause({ ...newClause, content: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none"
                  maxLength={1000}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Título: {newClause.title.length}/100 • Conteúdo: {newClause.content.length}/1000
                  </span>
                  <button
                    type="button"
                    onClick={addClause}
                    disabled={!newClause.title.trim() || !newClause.content.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Cláusula
                  </button>
                </div>
              </div>
            </div>

            {customClauses.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📋</div>
                <p className="text-sm">Nenhuma cláusula personalizada adicionada</p>
                <p className="text-xs mt-1">Use o formulário acima para adicionar cláusulas específicas para este contrato</p>
              </div>
            )}
          </div>

          {/* Dica */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-3">
              <div className="text-amber-600 text-xl">💡</div>
              <div>
                <h5 className="font-medium text-amber-800 mb-1">Dica</h5>
                <p className="text-amber-700 text-sm leading-relaxed">
                  As cláusulas personalizadas são úteis para incluir condições específicas do projeto, 
                  como penalidades, condições especiais de garantia, ou requisitos técnicos particulares. 
                  Elas aparecerão no contrato final após as cláusulas padrão.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EbesClausesManager;